import React, { useMemo } from 'react';

import { createStyles, Divider, Group, Input, Text, TextInput } from '@mantine/core';
import HelpButton from 'components/HelpButton';
import { useLocalization } from 'context/localization-context';

const useStyles = createStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: '3fr 5fr',
    gap: theme.spacing.xs,
    justifyItems: 'stretch',
    alignItems: 'start',
    padding: `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.lg}`,
  },
}));

interface ISessionValueDisplayProps {
  totalEstimatedSessions: number | null;
  sessionValue: number | null;
  totalPrice: number;
  isEditing: boolean;
  isTaxInclusive: boolean;
}

const SessionValueDisplay = ({
  totalEstimatedSessions,
  sessionValue,
  totalPrice,
  isEditing,
  isTaxInclusive,
}: ISessionValueDisplayProps): JSX.Element => {
  const { formatCurrency } = useLocalization();
  const { classes } = useStyles();

  const sessionText = `Session Value is the pre-tax revenue per credit, calculated by dividing the Total Price by the number of sessions.`;
  const sessionTextVat =
    'Session Value is the pre-VAT revenue per credit, calculated by dividing the Total Price (ex. VAT) by the number of sessions.';

  const getInputValue = useMemo(() => {
    if (totalEstimatedSessions === null)
      return 'Session value not available (all rules have unlimited credits)';

    if (!totalPrice) return 'Enter Total Price to see Session Value';

    const formattedPrice = formatCurrency(totalPrice);

    if (isEditing) {
      const priceLabel = isTaxInclusive ? `${formattedPrice} ex. VAT` : formattedPrice;
      return `${priceLabel} ÷ ${totalEstimatedSessions} Sessions = ${formatCurrency(sessionValue ?? 0)}`;
    }

    return formatCurrency(sessionValue ?? 0);
  }, [formatCurrency, sessionValue, totalEstimatedSessions, totalPrice, isEditing, isTaxInclusive]);

  return (
    <>
      <Divider my='xs' mx='lg' />
      <div className={classes.grid}>
        <Group noWrap spacing={2}>
          <Input.Label ta='right' my='0.25rem'>
            Session Value
          </Input.Label>
          <HelpButton iconWidth={20} position='left' width={280}>
            <Text size='sm'>
              {isTaxInclusive ? sessionTextVat : sessionText}
              <strong> This value serves as the basis for all percentage-based coach payouts.</strong>
            </Text>
          </HelpButton>
        </Group>
        <TextInput size='xs' required disabled={true} value={getInputValue} mr={'0.5rem'} />
      </div>
    </>
  );
};

export default SessionValueDisplay;
